import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FloppyDisk, Plus, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AppHeader } from "@/components/AppHeader";
import { api, formatApiErrorDetail } from "@/lib/api";

const FLAT = [
  ["backsplash_per_sqft", "Backsplash $/ft²"],
  ["install_per_sqft", "Install $/ft²"],
  ["template_fee", "Template fee $"],
  ["min_sqft", "Minimum billed ft²"],
  ["waste_pct", "Waste %"],
  ["tax_rate", "Tax %"],
  ["labor_cost_per_sqft", "Shop labor cost $/ft² (margin estimate)"],
  ["min_margin_pct", "Minimum line margin % (flags low-margin quote lines)"],
  ["default_markup_pct", "Default markup % in the Cost & Margin calculator"],
  ["slab_markup", "Slab cost markup × (AI quoting; 0 = use group multiplier)"],
  ["edge_lf_per_sqft", "Assumed edge run, LF per ft² (AI quoting, default 0.85)"],
];

export default function PricingSettings() {
  const [pricing, setPricing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newRate, setNewRate] = useState({ name: "", price: "" });

  useEffect(() => {
    api.get("/settings/pricing").then(({ data }) => setPricing(data.pricing));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings/pricing", pricing);
      toast.success("Pricing saved — quotes will use these rates");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not save pricing",
      );
    }
    setBusy(false);
  };

  if (!pricing)
    return (
      <div className="grain min-h-screen">
        <AppHeader title="Pricing" />
      </div>
    );

  const slug = (s) => s.toLowerCase().replace(/ /g, "-");

  const addRate = (key) => {
    const name = newRate.name.trim();
    if (!name) {
      toast.error("Give the cut-out type a name");
      return;
    }
    if (pricing[key]?.[name] !== undefined) {
      toast.error(`${name} is already on the list`);
      return;
    }
    setPricing({
      ...pricing,
      [key]: { ...pricing[key], [name]: Number(newRate.price) || 0 },
    });
    setNewRate({ name: "", price: "" });
  };

  const group = (key, label, editable = false, hint = "") => (
    <div
      key={label}
      className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
    >
      <div className="text-xs uppercase tracking-widest text-zinc-500">
        {label}
      </div>
      {hint && (
        <p className="mt-1.5 max-w-3xl text-[11px] leading-relaxed text-zinc-600">
          {hint}
        </p>
      )}
      <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        {Object.entries(pricing[key]).map(([k, v]) => (
          <div key={k} className="space-y-1">
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
              {k}
            </Label>
            <div className="flex items-center gap-1">
              <Input
                data-testid={`price-${key}-${slug(k)}`}
                type="number"
                step="0.01"
                value={v}
                onChange={(e) =>
                  setPricing({
                    ...pricing,
                    [key]: { ...pricing[key], [k]: Number(e.target.value) },
                  })
                }
                className="h-11 border-zinc-800 bg-zinc-950"
              />
              {editable && (
                <button
                  type="button"
                  data-testid={`price-${key}-remove-${slug(k)}`}
                  title={`Remove ${k}`}
                  onClick={() => {
                    const next = { ...pricing[key] };
                    delete next[k];
                    setPricing({ ...pricing, [key]: next });
                  }}
                  className="rounded-md border border-zinc-700 p-2.5 text-zinc-500 transition-colors duration-150 hover:border-red-500/40 hover:text-red-400"
                >
                  <Trash size={14} weight="bold" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      {editable && (
        <div
          className="mt-4 flex flex-wrap items-end gap-2 border-t border-zinc-800 pt-4"
          data-testid={`price-${key}-new-row`}
        >
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
              Your own type
            </Label>
            <Input
              data-testid={`price-${key}-new-name`}
              value={newRate.name}
              onChange={(e) => setNewRate({ ...newRate, name: e.target.value })}
              placeholder="Vessel sink"
              className="h-11 w-56 border-zinc-800 bg-zinc-950"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
              $ each
            </Label>
            <Input
              data-testid={`price-${key}-new-price`}
              type="number"
              step="0.01"
              value={newRate.price}
              onChange={(e) =>
                setNewRate({ ...newRate, price: e.target.value })
              }
              placeholder="0.00"
              className="h-11 w-32 border-zinc-800 bg-zinc-950"
            />
          </div>
          <Button
            data-testid={`price-${key}-add`}
            variant="outline"
            onClick={() => addRate(key)}
            className="h-11 border-zinc-700 bg-transparent text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-800"
          >
            <Plus size={14} weight="bold" className="mr-1.5" /> Add
          </Button>
          <span className="text-[11px] text-zinc-500">
            Save, and your own cut-outs show up on the drawing and price
            themselves on every quote.
          </span>
        </div>
      )}
    </div>
  );

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Settings / Pricing" />
      <main className="relative z-10 pb-28 pt-6">
        <Link
          to="/settings"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-white"
        >
          <ArrowLeft size={14} weight="bold" /> Settings
        </Link>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-600">
              Workspace
            </div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
              Quote pricing
            </h1>
            <p className="mt-1 text-sm text-zinc-500">
              Edge profiles, cut-outs, trade discounts and shop fees used by
              every quote
            </p>
          </div>
          <Button
            data-testid="save-pricing-button"
            disabled={busy}
            onClick={save}
            className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
          >
            <FloppyDisk size={16} weight="bold" className="mr-2" />{" "}
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>

        <div className="mt-6 space-y-4">
          {group("edges", "Edge profiles — $ per linear ft")}
          {group("cutouts", "Cut-outs — $ each", true)}
          {group("trade_discounts", "Trade discount % by account group")}
          {group(
            "natural_multipliers",
            "Natural stone with no price — cost × this, by customer group",
            false,
            "Only for natural stone: granite, marble, quartzite, onyx, soapstone, dolomite, travertine, limestone, slate, sandstone, basalt. Used when a slab has no group price and no sale price under Inventory. Set 0 to show nothing instead.",
          )}
          <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  Quote profit strip
                </div>
                <p className="mt-1.5 max-w-3xl text-[11px] leading-relaxed text-zinc-600">
                  Show total cost, profit and margin next to the quote total on the
                  Quote and Review steps. Staff only — never shown on the customer&apos;s
                  quote, PDF or portal.
                </p>
              </div>
              <button
                type="button"
                data-testid="price-show-profit-strip"
                onClick={() =>
                  setPricing({
                    ...pricing,
                    show_profit_strip: pricing.show_profit_strip === false,
                  })
                }
                className={`h-10 shrink-0 rounded-full px-6 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                  pricing.show_profit_strip === false
                    ? "bg-zinc-800 text-zinc-400"
                    : "bg-emerald-500/20 text-emerald-300"
                }`}
              >
                {pricing.show_profit_strip === false ? "Off" : "On"}
              </button>
            </div>
          </div>
          <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
            <div className="text-xs uppercase tracking-widest text-zinc-500">
              Shop fees
            </div>
            <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {FLAT.map(([k, label]) => (
                <div key={k} className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                    {label}
                  </Label>
                  <Input
                    data-testid={`price-${k}`}
                    type="number"
                    step="0.01"
                    value={pricing[k]}
                    onChange={(e) =>
                      setPricing({ ...pricing, [k]: Number(e.target.value) })
                    }
                    className="h-11 border-zinc-800 bg-zinc-950"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
